/**
 * Animatedly swaps an image with fade-out/fade-in effect
 * @param {HTMLImageElement} imgElement - Image element to swap
 * @param {string} newUrl - URL of the new image
 * @returns {Promise<void>}
 */
export async function swapImage(imgElement, newUrl) {
    // 0. Verify image exists
    try {
        await preloadImage(newUrl);
    } catch {
        return; // Image load failed, do nothing
    }

    // 1. Add fade-out animation class
    imgElement.classList.add('bp-img--fade-out');

    // 2. Wait for animation to complete
    await new Promise(resolve => {
        imgElement.addEventListener('animationend', resolve, { once: true });
    });

    // 3. Swap image URL - update all sources and img attributes
    const parent = imgElement.parentElement;
    if (parent && parent.tagName === 'PICTURE') {
        // Update all source tags
        parent.querySelectorAll('source').forEach(source => {
            source.setAttribute('srcset', generateSrcset(newUrl));
        });
    }

    // Update img element
    imgElement.src = newUrl;
    imgElement.setAttribute('srcset', generateSrcset(newUrl));

    // Remove fade-out class (image now animates with fade-in due to :load state)
    imgElement.classList.remove('bp-img--fade-out');
}

/**
 * Generates srcset string in format "path/name.ext, path/name@2x.ext 2x"
 * @param {string} imageUrl - Image URL
 * @returns {string} Generated srcset
 */
export function generateSrcset(imageUrl) {
    const lastSlash = imageUrl.lastIndexOf('/');
    const path = imageUrl.substring(0, lastSlash + 1);
    const filename = imageUrl.substring(lastSlash + 1);

    const lastDot = filename.lastIndexOf('.');
    const name = filename.substring(0, lastDot);
    const ext = filename.substring(lastDot + 1);

    return `${path}${name}.${ext}, ${path}${name}@2x.${ext} 2x`;
}

/**
 * Preloads an image into browser cache
 * @param {string} url - Image URL
 * @returns {Promise<void>}
 */
function preloadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
    });
}

